// components/OnchainTransactionsLine.tsx
import { observer } from "mobx-react";
import React from "react";
import { Timeline, Text, Group, ActionIcon, Badge, Box } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconLink,
  IconExternalLink,
  IconClock,
  IconAbacus,
} from "@tabler/icons-react";
import { Bob } from "../../stores/Bob";
import { TransactionListOutput } from "../../helpers/transactionsList";
import { TransactionsBullet, BULLET_SIZE } from "./transactionsBullet";

interface OnchainTransactionsLineProps {
  line: TransactionListOutput;
  bob: Bob;
}

const OnchainTransactionsLine = observer(
  ({ line, bob }: OnchainTransactionsLineProps) => {
    const isMobile = useMediaQuery("(max-width: 480px)");
    const primarySize = isMobile ? "sm" : "md";
    const subSize = isMobile ? "xs" : "sm";

    const isConfirmed = line.isConfirmed;
    const isOutbound = line.internalModelName === "OnchainOutboundTransaction";
    const arrowColor = isOutbound ? "gray" : "blue";

    const confirmationsText = "pending";

    const mempoolTxUrl = line.onchainTransactionId
      ? `https://mempool.space/tx/${line.onchainTransactionId}`
      : null;

    const truncatedTxId = line.onchainTransactionId
      ? `${line.onchainTransactionId.slice(0, 8)}...`
      : "";

    const bullet = (
      <TransactionsBullet
        imgSrc={null}
        bg={`var(--mantine-color-${arrowColor}-filled)`}
        badgeColor={arrowColor as "gray" | "blue"}
        centerIcon={
          isConfirmed ? <IconLink size={18} /> : <IconClock size={18} />
        }
      />
    );

    return (
      <Timeline.Item
        bullet={bullet}
        style={{ ["--tl-bullet-size" as any]: `${BULLET_SIZE}px` }}
      >
        <Group
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: "8px",
            maxWidth: "100%",
            overflow: "hidden",
            padding: "0 12px",
          }}
        >
          <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
            <Group gap={4} align="center" wrap="nowrap">
              <Text
                size={primarySize}
                style={{
                  wordBreak: "break-word",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: `var(--mantine-color-${arrowColor}-text)`,
                }}
              >
                {line.label}
              </Text>
              {!isConfirmed && (
                <Badge size="xs" color="orange" variant="light">
                  {confirmationsText}
                </Badge>
              )}
            </Group>

            <Text size={subSize} c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {line.createdAtString}
            </Text>

            {mempoolTxUrl && (
              <a
                href={mempoolTxUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "none",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Text
                  size={subSize}
                  c="blue"
                  style={{
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {truncatedTxId}
                </Text>
                <IconExternalLink
                  size={12}
                  style={{
                    opacity: 0.6,
                    color: "var(--mantine-color-blue-text)",
                  }}
                />
              </a>
            )}
          </div>

          <Group
            style={{
              flex: "0 0 auto",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                flex: "0 0 auto",
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              <Text
                size={isMobile ? "md" : "lg"}
                style={{ color: `var(--mantine-color-${arrowColor}-text)` }}
              >
                {line.satoshisString}
              </Text>
              <Text size={subSize} c="dimmed" style={{ whiteSpace: "nowrap" }}>
                {line.fiatString}
              </Text>
            </div>

            <ActionIcon
              variant="subtle"
              aria-label="data"
              color="gray"
              style={{
                flexShrink: 0,
                opacity: 0.85,
                width: 20,
                height: 20,
                minWidth: 20,
              }}
              onClick={() =>
                bob.setSelectedTransactionToView({
                  modelName: line.internalModelName,
                  uuid: line.internalUuid,
                })
              }
            >
              <IconAbacus size={14} style={{ opacity: 0.75 }} stroke={1.5} />
            </ActionIcon>
          </Group>
        </Group>
      </Timeline.Item>
    );
  }
);

export default OnchainTransactionsLine;
